<script lang="ts">
  import { getContext, onMount } from "svelte"
  import { derived } from "svelte/store"
  import IntegrationTypes from "../constants/IntegrationTypes"
  import AlertAddIcon from "../Icons/alert_add.svg?raw"
  import AlertSetIcon from "../Icons/alert_set.svg?raw"
  import MarkEmailReadIcon from "../Icons/mark_email_read.svg?raw"
  import type store from "website_script/stores/app"
  import { PriceAlertStatus } from "website_script/stores/app"
  import PriceAlertView from "website_script/components/PriceAlertView.svelte"
  import type { Texts } from "website_script/types/texts"
  import type { Environment } from "website_script/types/environment"
  import type { ElementData } from "website_script/lib/Parsers"
  import AuText from "component_library/components/svelte/ui/AUText.svelte"
  import { getFlowsContext } from "website_script/contexts/flows"
  import logo from "icons/branding/autouncle-logo-color.svg?raw"
  import Svg from "website_script/components/Svg.svelte"

  const texts = getContext<Texts>("texts")
  const appStore = getContext<typeof store>("appStore")
  const { trackFunction } = getContext<Environment>("environment")
  const { triggerFlow } = getFlowsContext()

  const priceAlertStatus = derived(
    appStore,
    ($appStore) => $appStore.priceAlert.status
  )
  export let elementData: ElementData

  /**
   * When clicking a button, track the interaction and open the modal
   */
  const handleClick = () => {
    triggerFlow(
      {
        flow_type: "price_alert",
        action_type: "input_form",
      },
      IntegrationTypes.PRICE_ALERT_WIDGET,
      undefined,
      elementData
    )
  }

  onMount(() => {
    trackFunction({
      action: "displayed",
      entity: "price-alert-widget",
      type: "displayed_cta",
      flow: "price_alert",
    })
  })
</script>

<!--
@component
Renders an area with the buttons enabled in the rules.
-->
<div class="auws-price-alert" role="complementary">
  <div class="auws-price-alert__content">
    <div class="auws-price-alert__cta auws-price-alert__section">
      {#if $priceAlertStatus === PriceAlertStatus.NeedsVerification}
        <PriceAlertView
          svgIcon={MarkEmailReadIcon}
          iconAltText="Alert pending"
          title={texts.priceAlertPendingTitle}
          subtitle={texts.priceAlertPendingSubtitle}
        />
      {:else if $priceAlertStatus === PriceAlertStatus.IsSet}
        <PriceAlertView
          svgIcon={AlertSetIcon}
          iconAltText="Alert set"
          title={texts.priceAlertSetTitle}
          subtitle={texts.priceAlertSetSubtitle}
        />
      {:else if $priceAlertStatus === PriceAlertStatus.NotSet}
        <PriceAlertView
          svgIcon={AlertAddIcon}
          iconAltText="Add alert"
          title={texts.priceAlertButtonText}
          subtitle={texts.priceAlertPromptText}
          clickHandler={handleClick}
        />
      {/if}
    </div>
    <hr class="auws-divider" />
    <div class="auws-price-alert__footer auws-price-alert__section">
      <AuText font="body-xsmall">{texts.priceAlertFooterText}</AuText>
      <Svg alt="AutoUncle" data={logo} height="14" />
    </div>
  </div>
</div>

<style>
  .auws-price-alert {
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: center;
    font-style: normal;
    border-radius: var(--au-border-radius-sharp);
    border: 1px solid var(--au-line-color-neutral);
    background: var(--au-background-default);
    box-shadow: var(--au-box-default-box-shadow);
  }

  .auws-price-alert__content {
    background: var(--au-background-neutral);
    border-radius: var(--au-border-radius-sharp);
    padding: var(--au-spacing-md) 0;
  }

  .auws-price-alert__cta {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .auws-price-alert__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .auws-divider {
    border: 0;
    border-top: 1px dashed var(--au-line-color-neutral);
    margin: var(--au-spacing-sm) 0;
  }

  .auws-price-alert__section {
    padding: 0 var(--au-spacing-md);
    gap: var(--au-spacing-xs);
  }
</style>